import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { cioFacetsToUrlFilters } from '~/grid/utils/cioUtils';
import { CIO_BROWSE_FACETS } from '~/shared/constants/queryCacheKeys';
import { useCioClient } from '~/shared/utils/useCioClient';

import getAllCioBrowseFacets from '../api/getAllCioBrowseFacets';
import { BROWSE_API_FACETS_STALE_TIME } from '../constants';

// We need to fetch all facets first to whitelist the selected facets from the url params
const useCioBrowseFacets = () => {
  const cioClient = useCioClient();

  const { asPath } = useRouter();
  const query = asPath.split('?')[1];

  const {
    data: allRegisteredFacetsRaw,
    // isLoading: allRegisteredFacetsIsLoading,
    // isFetching: allRegisteredFacetsIsFetching,
    // error: allRegisteredFacetsError,
  } = useQuery({
    queryKey: [CIO_BROWSE_FACETS],
    queryFn: () => {
      return getAllCioBrowseFacets({ cioClient });
    },
    enabled: Boolean(cioClient),
    staleTime: BROWSE_API_FACETS_STALE_TIME,
  });

  const allRegisteredFacets = allRegisteredFacetsRaw?.response?.facets;

  const memoizedAllRegisteredFacetNames = useMemo(() => {
    return allRegisteredFacets?.map((facet) => facet?.name);
  }, [allRegisteredFacets]);

  const memoizedSelectedUrlFilters = useMemo(() => {
    const selectedFacets = {};
    const urlParams = new URLSearchParams(query);

    if (memoizedAllRegisteredFacetNames?.length > 0) {
      memoizedAllRegisteredFacetNames.forEach((facetName) => {
        const mappedFacetName = cioFacetsToUrlFilters[facetName] || facetName;
        const facetValue = urlParams.get(mappedFacetName);
        if (facetValue) {
          selectedFacets[mappedFacetName] = facetValue.split(',');
        }
      });
    }
    return selectedFacets;
  }, [memoizedAllRegisteredFacetNames, query]);

  return {
    allRegisteredFacets,
    allRegisteredFacetNames: memoizedAllRegisteredFacetNames,
    selectedUrlFilters: memoizedSelectedUrlFilters,
  };
};

export default useCioBrowseFacets;
